import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    RefreshCcw,
    LogOut,
    Check,
    EyeOff,
    Trash2,
    Star,
    StarOff,
    Quote,
    Clock,
    Heart,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const TABS = [
    { key: "pending", label: "Pendientes", icon: Clock, color: "text-[var(--warning)]" },
    { key: "approved", label: "Aprobados", icon: Check, color: "text-[var(--paper-2)]" },
    { key: "hidden", label: "Ocultos", icon: EyeOff, color: "text-[var(--ink-muted)]" },
];

export default function AdminTestimonios() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/testimonios?status=${tab}&limit=200`);
            setItems(Array.isArray(data) ? data : []);
        } catch {
            toast.error("No se pudo cargar la lista.");
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        reload();
    }, [reload]);

    async function patch(id, body) {
        try {
            await api.patch(`/admin/testimonios/${id}`, body);
            toast.success("Actualizado.");
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo actualizar.");
        }
    }

    async function remove(id) {
        if (!confirm("¿Eliminar este testimonio? Es irreversible.")) return;
        try {
            await api.delete(`/admin/testimonios/${id}`);
            toast.success("Eliminado.");
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo eliminar.");
        }
    }

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    return (
        <>
            <SEO title="Admin · Testimonios" description="Moderación de testimonios" path="/admin/testimonios" />
            <section className="py-12 md:py-16" data-testid="admin-testimonios">
                <div className="container-x max-w-6xl">
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-10 pb-8 border-b border-[var(--line)]">
                        <div>
                            <Link
                                to="/admin"
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-4"
                                data-testid="admin-testimonios-back"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Volver al panel
                            </Link>
                            <p className="eyebrow mb-3">Departamento · Testimonios</p>
                            <h1 className="font-display text-4xl md:text-6xl text-[var(--paper-2)] leading-[0.95]">
                                Voces & <span className="text-[var(--danger)]">moderación</span>
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                Sesión: {user?.email}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={reload}
                                className="btn-brutal btn-brutal-ghost"
                                disabled={loading}
                                data-testid="testimonios-reload"
                            >
                                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                Recargar
                            </button>
                            <button onClick={handleLogout} className="btn-brutal btn-brutal-ghost">
                                <LogOut className="w-4 h-4" />
                                Salir
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-8" data-testid="testimonios-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`tab-${t.key}`}
                                className={[
                                    "font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2.5 border-2 transition-all",
                                    tab === t.key
                                        ? "border-[var(--danger)] bg-[var(--danger)] text-[var(--paper-2)]"
                                        : "border-[var(--line)] text-[var(--ink-muted)] hover:text-[var(--paper-2)]",
                                ].join(" ")}
                            >
                                <t.icon className="w-3 h-3 inline mr-2" />
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <p className="font-mono text-xs text-[var(--ink-muted)]">Cargando…</p>
                    ) : items.length === 0 ? (
                        <div className="border border-dashed border-[var(--ink-dim)] p-10 text-center">
                            <Quote className="w-10 h-10 text-[var(--ink-dim)] mx-auto mb-3" />
                            <p className="font-mono text-sm text-[var(--ink-muted)]">
                                Sin testimonios en esta vista.
                            </p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-6" data-testid="testimonios-list">
                            {items.map((t) => (
                                <article
                                    key={t.id}
                                    className="border border-[var(--line)] bg-[#0d0d0d] p-6 relative"
                                    data-testid={`admin-testimonio-${t.id}`}
                                >
                                    {t.featured && (
                                        <span className="absolute -top-2 -left-2 bg-[var(--warning)] text-black font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1">
                                            Destacado
                                        </span>
                                    )}
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-3">
                                        {(t.created_at || "").slice(0, 16).replace("T", " ")} · {t.source}
                                    </p>
                                    <p className="font-display italic text-base md:text-lg text-[var(--paper-2)] leading-[1.5] mb-4">
                                        &ldquo;{t.message}&rdquo;
                                    </p>
                                    <div className="border-t border-dashed border-[var(--ink-dim)] pt-3 mb-4">
                                        <p className="font-mono text-sm text-[var(--paper)] font-bold">
                                            {t.name}
                                        </p>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-1">
                                            {t.location || "—"}
                                            {t.role ? ` · ${t.role}` : ""}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {tab !== "approved" && (
                                            <button
                                                onClick={() => patch(t.id, { status: "approved" })}
                                                className="p-2 border border-[var(--warning)] text-[var(--warning)] hover:bg-[var(--warning)] hover:text-black inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-3"
                                                data-testid={`approve-${t.id}`}
                                                title="Aprobar"
                                            >
                                                <Check className="w-3.5 h-3.5" /> Aprobar
                                            </button>
                                        )}
                                        {tab !== "hidden" && (
                                            <button
                                                onClick={() => patch(t.id, { status: "hidden" })}
                                                className="p-2 border border-[var(--ink-muted)] text-[var(--ink-muted)] hover:bg-[var(--ink-muted)] hover:text-black inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-3"
                                                data-testid={`hide-${t.id}`}
                                                title="Ocultar"
                                            >
                                                <EyeOff className="w-3.5 h-3.5" /> Ocultar
                                            </button>
                                        )}
                                        <button
                                            onClick={() => patch(t.id, { featured: !t.featured })}
                                            className={`p-2 border inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-3 ${
                                                t.featured
                                                    ? "border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)]"
                                                    : "border-[var(--paper)] text-[var(--paper)] hover:bg-[var(--paper)] hover:text-black"
                                            }`}
                                            data-testid={`featured-${t.id}`}
                                            title={t.featured ? "Quitar destacado" : "Destacar"}
                                        >
                                            {t.featured ? <StarOff className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
                                            {t.featured ? "Quitar" : "Destacar"}
                                        </button>
                                        <button
                                            onClick={() => remove(t.id)}
                                            className="p-2 border border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)] inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-3 ml-auto"
                                            data-testid={`delete-${t.id}`}
                                            title="Eliminar"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}

                    <div className="mt-10 border border-dashed border-[var(--warning)] bg-[#0d0d0d] p-5 max-w-3xl">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-2 flex items-center gap-2">
                            <Heart className="w-3 h-3" />
                            Cómo funciona
                        </p>
                        <p className="font-mono text-xs text-[var(--paper)] leading-relaxed">
                            Los testimonios entran como <em>pending</em>. Apruébalos
                            para que aparezcan públicamente en Home, /libro y
                            /descargar. <em>Destacar</em> los muestra primero
                            con badge dorado. Rate-limit anti-spam: 3 envíos
                            por hora por IP.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
